import Pristine from 'pristinejs';
import { getCaptchaAction, loadRecaptcha } from '../captcha/google';
import { getHTML, getUtilityPopupContainer } from '../popups/utility-popups';
import { closeAllUtilityPopups, openUtilityPopup } from '../popups/utils';
import { closeSidenav } from '../navigation/side-nav';
import newValidator, { FormValidationError } from '../validation';
import triggerEvent, { ErrorEvent, ErrorType, GAEventName } from '../analytics/ga/events';

type ReportPropertyErrorData = {
    email: string;
    selection: number;
    comments: string;
    property: number;
    agentId: string;
    agentRef: string;
    recaptcha: string;
    recaptchaAction: string;
}

function initPropertyErrorReport( btn: HTMLButtonElement ) {
    if ( btn.dataset.initialised && btn.dataset.initialised === 'true' ) {
        return;
    }

    btn.dataset.initialised = 'true';

    let validator: Pristine | null = null;

    btn.addEventListener( 'click', async ( event: Event ) => {
        let popup = document.querySelector( '.popup__container--report-property-error' );

        if ( !popup ) {
            const popupContainer = getUtilityPopupContainer();
            popup = await getHTML( 'property-error-report-popup', {
                'propertyID': ( document.querySelector( '.popup-property' ) as HTMLElement ).dataset.propertyId || '',
            } );
            popupContainer.appendChild( popup );
        }

        const form = popup.querySelector( 'form' ) as HTMLFormElement;

        closeSidenav();

        closeAllUtilityPopups( false );

        openUtilityPopup( popup );

        if ( !form.dataset.initialised ) {
            if ( !validator ) {
                validator = newValidator( form );
            }

            const email = form.querySelector( '[name="email"]' ) as HTMLInputElement;
            const selection = form.querySelector( '[name="errortype"]' ) as HTMLSelectElement;
            const comments = form.querySelector( '[name="comments"]' ) as HTMLTextAreaElement;
            const btnSubmit = form.querySelector( 'button[type="submit"]' ) as HTMLButtonElement;
            const btnClose = form.querySelector( 'button[type="button"]' ) as HTMLButtonElement;
            const btnExit = popup.querySelector( '.popup__close' ) as HTMLButtonElement;
            const messageContainer = form.querySelector( '.message-container' ) as HTMLElement;

            const onClose = () => {
                form.reset();
                validator.reset();
                messageContainer.innerHTML = '';
                ( email.closest( '.form-group' ) as HTMLElement ).classList.remove( 'd-none' );
                ( selection.closest( '.form-group' ) as HTMLElement ).classList.remove( 'd-none' );
                comments.classList.remove( 'd-none' );
                btnSubmit.classList.remove( 'd-none' );
                btnClose.classList.add( 'd-none' );
            };

            [ btnClose, btnExit ].forEach( ( btn: HTMLButtonElement ) => {
                btn.addEventListener( 'click', ( event: Event ) => {
                    closeAllUtilityPopups();
                    onClose();
                } );
            } );

            popup.addEventListener( 'twc:popup:close-utility-popup', () => onClose() );

            form.addEventListener( 'submit', async ( event: SubmitEvent ) => {
                event.preventDefault();

                btnSubmit.disabled = true;

                if ( form.dataset.isProcessing && form.dataset.isProcessing === 'true' ) {
                    btnSubmit.disabled = false;
                    return;
                }

                form.dataset.isProcessing = 'true';

                validator.reset();

                const valid = validator.validate();
                const errors: Array<FormValidationError> = validator.getErrors();

                if ( errors.length > 0 ) {
                    errors.forEach( ( err: FormValidationError ) => {
                        triggerEvent<ErrorEvent>( GAEventName.Error, {
                            'error_type': ErrorType.ErrForm,
                            'error_message': err.errors[ 0 ],
                            'engaged_target': err.input.value,
                            'engaged_element': err.input.name,
                        } );
                    } );
                }

                if ( !valid ) {
                    btnSubmit.disabled = false;
                    form.dataset.isProcessing = 'false';
                    return;
                }

                messageContainer.innerHTML = '';

                const property = form.querySelector( '[name="property"]' ) as HTMLInputElement;
                const agentId = form.querySelector( '[name="agentId"]' ) as HTMLInputElement;
                const agentRef = form.querySelector( '[name="agentRef"]' ) as HTMLInputElement;

                const data: ReportPropertyErrorData = {
                    email: email.value.trim(),
                    selection: parseInt( selection.value ),
                    comments: comments.value.trim() || '',
                    property: parseInt( property.value ),
                    agentId: agentId.value,
                    agentRef: agentRef.value,
                    recaptcha: await loadRecaptcha( getCaptchaAction( form ) ),
                    recaptchaAction: getCaptchaAction( form ),
                };

                const response = await window.http( form.getAttribute( 'action' ) as string, {
                    method: form.getAttribute( 'method' ) as string,
                    body: JSON.stringify( data ),
                    headers: {
                        'Content-Type': 'application/json',
                    },
                } );

                if ( !response.ok ) {
                    messageContainer.insertAdjacentHTML( 'beforeend', `<div class="alert mb-none alert-danger">${form.dataset.formGenericError}</div>` );
                    btnSubmit.disabled = false;
                    form.dataset.isProcessing = 'false';
                    return;
                }

                const json = await response.json();

                messageContainer.insertAdjacentHTML( 'beforeend', `<div class="alert mb-none alert-success">${json.message}</div>` );

                ( email.closest( '.form-group' ) as HTMLElement ).classList.add( 'd-none' );
                ( selection.closest( '.form-group' ) as HTMLElement ).classList.add( 'd-none' );
                comments.classList.add( 'd-none' );
                btnSubmit.classList.add( 'd-none' );
                btnClose.classList.remove( 'd-none' );
                form.dataset.isProcessing = 'false';
            } );
        }
    } );
}

export default initPropertyErrorReport;
